// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';

// material ui
import {
  Box,
  Divider,
  IconButton,
  AppBar as MuiAppBar,
  Stack,
  Toolbar,
  Tooltip,
} from '@mui/material';

// shared components
import { SuperuserOnly } from '@/components/access-control';

// components
import AppBreadcrumbs from '@/app/AppBreadcrumbs';

// relative
import AccountSelect from '../components/appbar/account-select/AccountSelect';
import AlarmNotifications from '../components/appbar/alarm-notification/AlarmNotifications';
import SettingsMenu from '../components/appbar/settings-menu/SettingsMenu';
import { ReleaseNotesIndicator } from '../components/appbar/update-banner';
import LogoComponent from '../components/navigation-items/LogoComponent';
import ChatButton from '../features/ai-assistent/components/ChatButton';
import { APPBAR_HEIGHT } from './constants';

interface AppBarProps {
  isMobile: boolean;
  desktopOpen: boolean;
  onMenuClick: () => void;
  frameBg: string;
  enableChatButton: boolean;
}

export default function AppBar({
  isMobile,
  desktopOpen,
  onMenuClick,
  frameBg,
  enableChatButton,
}: AppBarProps) {
  const { t } = useTranslation();

  return (
    <MuiAppBar
      position="fixed"
      elevation={0}
      sx={{
        height: APPBAR_HEIGHT,
        bgcolor: frameBg,
        color: 'text.primary',
        borderRadius: 0,
        zIndex: (t) => t.zIndex.drawer,
      }}
    >
      <Toolbar sx={{ minHeight: APPBAR_HEIGHT, ml: -0.5 }}>
        <Tooltip
          title={
            isMobile
              ? t('actions.showItem', { item: 'navigation' })
              : desktopOpen
                ? t('actions.hideItem', { item: 'navigation' })
                : t('actions.showItem', { item: 'navigation' })
          }
        >
          <IconButton onClick={onMenuClick} data-testid="nav-toggle-button">
            {desktopOpen ? <MenuOpenRoundedIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>

        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <LogoComponent />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            flex: 1,
            gap: 1,
          }}
        >
          <React.Suspense>
            <AccountSelect />
          </React.Suspense>
          <React.Suspense>
            <AppBreadcrumbs />
          </React.Suspense>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <SuperuserOnly>
            <React.Suspense>
              {enableChatButton && <ChatButton />}
            </React.Suspense>
            <SuperuserOnly>
              <AlarmNotifications />
            </SuperuserOnly>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: 'divider' }}
            />
          </SuperuserOnly>
          <React.Suspense>
            <ReleaseNotesIndicator />
          </React.Suspense>

          <SettingsMenu />
        </Stack>
      </Toolbar>
    </MuiAppBar>
  );
}
